import React, { useRef, useState, useEffect } from 'react';
import {
  Bold, Italic, Underline, Strikethrough, Heading1, Heading2, Heading3,
  AlignLeft, AlignCenter, AlignRight, AlignJustify,
  List, ListOrdered, Link as LinkIcon, Image as ImageIcon,
  Video, Table, Quote, Code, RemoveFormatting, Code2, Upload, X, Check
} from 'lucide-react';
import { adminApi } from '../../lib/adminApi';
import { getBlogImageUrl } from '../../lib/utils';
import AdminBlogUploads from './AdminBlogUploads';

export default function BlogRichEditor({ value, onChange, placeholder = "Write your blog post content here..." }) {
  const editorRef = useRef(null);
  const [isHtmlMode, setIsHtmlMode] = useState(false);
  const [htmlContent, setHtmlContent] = useState(value || '');
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [savedSelection, setSavedSelection] = useState(null);

  const [showImageModal, setShowImageModal] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [imageAlt, setImageAlt] = useState('');
  const [uploadingImg, setUploadingImg] = useState(false);

  const [showVideoModal, setShowVideoModal] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');

  // Keep internal state in sync with external value
  useEffect(() => {
    if (value !== htmlContent) {
      setHtmlContent(value || '');
      if (editorRef.current && !isHtmlMode && editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value || '';
      }
    }
  }, [value]);

  const handleInput = () => {
    if (editorRef.current) {
      const content = editorRef.current.innerHTML;
      setHtmlContent(content);
      if (onChange) onChange(content);
    }
  };

  const handleHtmlChange = (e) => {
    const val = e.target.value;
    setHtmlContent(val);
    if (onChange) onChange(val);
  };

  const toggleHtmlMode = () => {
    if (!isHtmlMode) {
      // Switching to HTML mode
      if (editorRef.current) {
        setHtmlContent(editorRef.current.innerHTML);
      }
      setIsHtmlMode(true);
    } else {
      // Switching to visual mode
      setIsHtmlMode(false);
      setTimeout(() => {
        if (editorRef.current) {
          editorRef.current.innerHTML = htmlContent;
        }
      }, 0);
    }
  };

  const execute = (command, val = null) => {
    if (isHtmlMode) return;
    if (editorRef.current) {
      editorRef.current.focus();
    }
    document.execCommand(command, false, val);
    handleInput();
  };

  const saveCurrentSelection = () => {
    const sel = window.getSelection();
    if (sel.getRangeAt && sel.rangeCount) {
      setSavedSelection(sel.getRangeAt(0));
      setLinkText(sel.toString());
    }
  };

  const restoreSelection = () => {
    if (savedSelection) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(savedSelection);
    }
  };

  // ── Link modal handlers ──
  const openLinkDialog = () => {
    saveCurrentSelection();
    setShowLinkModal(true);
  };

  const applyLink = () => {
    restoreSelection();
    if (!linkUrl) {
      setShowLinkModal(false);
      return;
    }
    const finalUrl = linkUrl.startsWith('http://') || linkUrl.startsWith('https://') || linkUrl.startsWith('/') 
      ? linkUrl 
      : `https://${linkUrl}`;

    if (savedSelection && savedSelection.toString().length > 0) {
      execute('createLink', finalUrl);
    } else {
      const textToInsert = linkText || finalUrl;
      const html = `<a href="${finalUrl}" target="_blank" rel="noopener noreferrer">${textToInsert}</a>`;
      execute('insertHTML', html);
    }
    setShowLinkModal(false);
    setLinkUrl('');
    setLinkText('');
  };

  // ── Image modal handlers ──
  const openImageDialog = () => {
    saveCurrentSelection();
    setShowImageModal(true);
  };

  const applyImage = () => {
    restoreSelection();
    if (imageUrl) {
      const finalSrc = getBlogImageUrl(imageUrl);
      const imgHtml = `<img src="${finalSrc}" alt="${imageAlt || 'Blog image'}" style="max-width:100%; height:auto; border-radius:8px; margin:16px 0;" />`;
      execute('insertHTML', imgHtml);
    }
    setShowImageModal(false);
    setImageUrl('');
    setImageAlt('');
  };

  const handleImageFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      alert("Image exceeds maximum size of 50MB.");
      return;
    }
    setUploadingImg(true);
    try {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await adminApi.post('/admin/blog-upload', formData);
        if (res.data?.url) {
          setImageUrl(getBlogImageUrl(res.data.url));
          return;
        }
      } catch (uploadErr) {
        console.warn('Multipart upload failed, falling back to base64:', uploadErr);
        const reader = new FileReader();
        const base64Promise = new Promise((resolve, reject) => {
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
        });
        reader.readAsDataURL(file);
        const dataUrl = await base64Promise;
        const res = await adminApi.post('/admin/blog-upload-base64', {
          data: dataUrl,
          filename: file.name,
        });
        if (res.data?.url) {
          setImageUrl(getBlogImageUrl(res.data.url));
          return;
        }
      }
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to upload image. Please try again.");
    } finally {
      setUploadingImg(false);
      e.target.value = '';
    }
  };

  // ── Video embed ──
  const openVideoDialog = () => {
    saveCurrentSelection();
    setShowVideoModal(true);
  };

  const applyVideo = () => {
    restoreSelection();
    if (videoUrl) {
      let embedUrl = videoUrl;
      // Handle YouTube
      const ytMatch = videoUrl.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([\w-]{11})/);
      if (ytMatch) {
        embedUrl = `https://www.youtube.com/embed/${ytMatch[1]}`;
      }
      const vHtml = `<div style="position:relative; padding-bottom:56.25%; height:0; overflow:hidden; margin:16px 0; border-radius:8px;"><iframe src="${embedUrl}" style="position:absolute; top:0; left:0; width:100%; height:100%; border:0;" allowfullscreen></iframe></div>`;
      execute('insertHTML', vHtml);
    }
    setShowVideoModal(false);
    setVideoUrl('');
  };

  // ── Insert Table ──
  const insertTable = () => {
    const tableHtml = `
      <table style="width:100%; border-collapse:collapse; margin:16px 0; border:1px solid #dfe5ec;">
        <thead>
          <tr style="background:#f8fafc;">
            <th style="border:1px solid #dfe5ec; padding:10px 14px; text-align:left;">Header 1</th>
            <th style="border:1px solid #dfe5ec; padding:10px 14px; text-align:left;">Header 2</th>
            <th style="border:1px solid #dfe5ec; padding:10px 14px; text-align:left;">Header 3</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style="border:1px solid #dfe5ec; padding:10px 14px;">Data 1</td>
            <td style="border:1px solid #dfe5ec; padding:10px 14px;">Data 2</td>
            <td style="border:1px solid #dfe5ec; padding:10px 14px;">Data 3</td>
          </tr>
          <tr>
            <td style="border:1px solid #dfe5ec; padding:10px 14px;">Data 4</td>
            <td style="border:1px solid #dfe5ec; padding:10px 14px;">Data 5</td>
            <td style="border:1px solid #dfe5ec; padding:10px 14px;">Data 6</td>
          </tr>
        </tbody>
      </table>
    `;
    execute('insertHTML', tableHtml);
  };

  return (
    <div className="blog-rich-editor border border-slate-300 rounded-lg overflow-hidden bg-white shadow-sm focus-within:border-amber-500 focus-within:ring-2 focus-within:ring-amber-200 transition-all">
      {/* Editor Toolbar matching Shopify Image 2 */}
      <div className="blog-toolbar bg-slate-50 border-b border-slate-200 p-2 flex flex-wrap items-center gap-1 text-slate-700">
        {/* Paragraph / Heading selector */}
        <select
          disabled={isHtmlMode}
          onChange={(e) => execute('formatBlock', e.target.value)}
          defaultValue="p"
          className="text-xs font-medium border border-slate-300 bg-white rounded px-2 py-1.5 focus:outline-none focus:border-amber-500 mr-1"
        >
          <option value="p">Paragraph</option>
          <option value="h1">Heading 1</option>
          <option value="h2">Heading 2</option>
          <option value="h3">Heading 3</option>
          <option value="h4">Heading 4</option>
          <option value="blockquote">Quote block</option>
          <option value="pre">Code block</option>
        </select>

        <span className="w-px h-5 bg-slate-300 mx-1" />

        {/* Text styling */}
        <button
          type="button"
          disabled={isHtmlMode}
          onClick={() => execute('bold')}
          title="Bold (Ctrl+B)"
          className="p-1.5 rounded hover:bg-slate-200 text-slate-700 disabled:opacity-40"
        >
          <Bold size={15} />
        </button>
        <button
          type="button"
          disabled={isHtmlMode}
          onClick={() => execute('italic')}
          title="Italic (Ctrl+I)"
          className="p-1.5 rounded hover:bg-slate-200 text-slate-700 disabled:opacity-40"
        >
          <Italic size={15} />
        </button>
        <button
          type="button"
          disabled={isHtmlMode}
          onClick={() => execute('underline')}
          title="Underline (Ctrl+U)"
          className="p-1.5 rounded hover:bg-slate-200 text-slate-700 disabled:opacity-40"
        >
          <Underline size={15} />
        </button>
        <button
          type="button"
          disabled={isHtmlMode}
          onClick={() => execute('strikeThrough')}
          title="Strikethrough"
          className="p-1.5 rounded hover:bg-slate-200 text-slate-700 disabled:opacity-40"
        >
          <Strikethrough size={15} />
        </button>

        <span className="w-px h-5 bg-slate-300 mx-1" />

        {/* Text Color */}
        <select
          disabled={isHtmlMode}
          onChange={(e) => execute('foreColor', e.target.value)}
          defaultValue="#172d45"
          className="text-xs border border-slate-300 bg-white rounded px-1.5 py-1 focus:outline-none"
          title="Text Color"
        >
          <option value="#172d45">Default</option>
          <option value="#f97316">Orange</option>
          <option value="#2563eb">Blue</option>
          <option value="#16a34a">Green</option>
          <option value="#dc2626">Red</option>
          <option value="#64748b">Slate</option>
        </select>

        {/* Alignment */}
        <button
          type="button"
          disabled={isHtmlMode}
          onClick={() => execute('justifyLeft')}
          title="Align Left"
          className="p-1.5 rounded hover:bg-slate-200 text-slate-700 disabled:opacity-40"
        >
          <AlignLeft size={15} />
        </button>
        <button
          type="button"
          disabled={isHtmlMode}
          onClick={() => execute('justifyCenter')}
          title="Align Center"
          className="p-1.5 rounded hover:bg-slate-200 text-slate-700 disabled:opacity-40"
        >
          <AlignCenter size={15} />
        </button>
        <button
          type="button"
          disabled={isHtmlMode}
          onClick={() => execute('justifyRight')}
          title="Align Right"
          className="p-1.5 rounded hover:bg-slate-200 text-slate-700 disabled:opacity-40"
        >
          <AlignRight size={15} />
        </button>
        <button
          type="button"
          disabled={isHtmlMode}
          onClick={() => execute('justifyFull')}
          title="Justify"
          className="p-1.5 rounded hover:bg-slate-200 text-slate-700 disabled:opacity-40"
        >
          <AlignJustify size={15} />
        </button>

        <span className="w-px h-5 bg-slate-300 mx-1" />

        {/* Media & Embeds */}
        <button
          type="button"
          disabled={isHtmlMode}
          onClick={openLinkDialog}
          title="Insert Link"
          className="p-1.5 rounded hover:bg-slate-200 text-slate-700 disabled:opacity-40"
        >
          <LinkIcon size={15} />
        </button>
        <button
          type="button"
          disabled={isHtmlMode}
          onClick={openImageDialog}
          title="Insert Image"
          className="p-1.5 rounded hover:bg-slate-200 text-slate-700 disabled:opacity-40"
        >
          <ImageIcon size={15} />
        </button>
        <button
          type="button"
          disabled={isHtmlMode}
          onClick={openVideoDialog}
          title="Embed Video"
          className="p-1.5 rounded hover:bg-slate-200 text-slate-700 disabled:opacity-40"
        >
          <Video size={15} />
        </button>
        <button
          type="button"
          disabled={isHtmlMode}
          onClick={insertTable}
          title="Insert Table"
          className="p-1.5 rounded hover:bg-slate-200 text-slate-700 disabled:opacity-40"
        >
          <Table size={15} />
        </button>

        <span className="w-px h-5 bg-slate-300 mx-1" />

        {/* Lists & Formatting */}
        <button
          type="button"
          disabled={isHtmlMode}
          onClick={() => execute('insertUnorderedList')}
          title="Bullet List"
          className="p-1.5 rounded hover:bg-slate-200 text-slate-700 disabled:opacity-40"
        >
          <List size={15} />
        </button>
        <button
          type="button"
          disabled={isHtmlMode}
          onClick={() => execute('insertOrderedList')}
          title="Numbered List"
          className="p-1.5 rounded hover:bg-slate-200 text-slate-700 disabled:opacity-40"
        >
          <ListOrdered size={15} />
        </button>
        <button
          type="button"
          disabled={isHtmlMode}
          onClick={() => execute('removeFormat')}
          title="Clear Formatting"
          className="p-1.5 rounded hover:bg-slate-200 text-slate-700 disabled:opacity-40"
        >
          <RemoveFormatting size={15} />
        </button>

        {/* Toggle HTML Source Code view */}
        <div className="ml-auto flex items-center">
          <button
            type="button"
            onClick={toggleHtmlMode}
            title={isHtmlMode ? "Switch to Visual Editor" : "View HTML Source"}
            className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded transition-colors ${
              isHtmlMode ? 'bg-amber-500 text-slate-900' : 'bg-slate-200 text-slate-800 hover:bg-slate-300'
            }`}
          >
            <Code2 size={14} />
            <span>{isHtmlMode ? 'Visual' : 'HTML'}</span>
          </button>
        </div>
      </div>

      {/* Editor Content Box */}
      {isHtmlMode ? (
        <textarea
          value={htmlContent}
          onChange={handleHtmlChange}
          className="w-full p-4 font-mono text-xs text-slate-900 bg-slate-900 text-emerald-400 min-h-[360px] focus:outline-none resize-y"
          placeholder="Type or paste raw HTML code..."
        />
      ) : (
        <div
          ref={editorRef}
          contentEditable
          onInput={handleInput}
          onBlur={handleInput}
          dangerouslySetInnerHTML={{ __html: htmlContent }}
          className="blog-wysiwyg-content p-5 min-h-[360px] max-h-[700px] overflow-y-auto focus:outline-none prose prose-slate max-w-none text-slate-800"
          style={{ minHeight: '360px' }}
        />
      )}

      {/* Modal Dialog: Insert Link */}
      {showLinkModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 space-y-4">
            <div className="flex justify-between items-center border-b pb-3">
              <h4 className="font-semibold text-slate-900 text-base">Insert Link</h4>
              <button onClick={() => setShowLinkModal(false)} className="text-slate-400 hover:text-slate-600">
                <X size={18} />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Link URL</label>
                <input
                  type="text"
                  placeholder="https://example.com"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  className="w-full text-sm border rounded-lg p-2.5 focus:border-amber-500"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Display Text (optional)</label>
                <input
                  type="text"
                  placeholder="Link text"
                  value={linkText}
                  onChange={(e) => setLinkText(e.target.value)}
                  className="w-full text-sm border rounded-lg p-2.5 focus:border-amber-500"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setShowLinkModal(false)}
                className="px-4 py-2 border rounded-lg text-xs font-semibold hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={applyLink}
                className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-slate-900 rounded-lg text-xs font-semibold"
              >
                Insert Link
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Dialog: Insert Image */}
      {showImageModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 space-y-4">
            <div className="flex justify-between items-center border-b pb-3">
              <h4 className="font-semibold text-slate-900 text-base">Insert Image</h4>
              <button onClick={() => setShowImageModal(false)} className="text-slate-400 hover:text-slate-600">
                <X size={18} />
              </button>
            </div>
            <div className="space-y-3">
              {/* Media Library Choose button */}
              <div>
                <button
                  type="button"
                  onClick={() => setShowMediaPicker(true)}
                  className="w-full py-2 px-3 border border-amber-500 bg-amber-50 hover:bg-amber-100 text-amber-900 rounded-lg text-xs font-semibold flex items-center justify-center gap-2 transition-colors shadow-xs"
                >
                  <ImageIcon size={15} />
                  Choose from Uploads
                </button>
              </div>

              <div className="relative flex py-0.5 items-center">
                <div className="flex-grow border-t border-slate-200"></div>
                <span className="flex-shrink mx-3 text-slate-400 text-[11px] font-medium uppercase tracking-wider">or upload from device</span>
                <div className="flex-grow border-t border-slate-200"></div>
              </div>

              {/* File upload option */}
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-3 text-center hover:border-amber-500 transition-colors bg-slate-50/50">
                <Upload className="mx-auto text-slate-400 mb-1" size={20} />
                <p className="text-xs text-slate-600 font-medium mb-1.5">Upload from computer</p>
                <input
                  type="file"
                  accept="image/*,.jfif,.avif"
                  onChange={handleImageFileUpload}
                  className="text-xs text-slate-500 file:mr-2 file:py-1 file:px-3 file:rounded file:border-0 file:text-xs file:font-semibold file:bg-amber-100 file:text-amber-800 hover:file:bg-amber-200 cursor-pointer"
                />
                {uploadingImg && <p className="text-xs text-amber-600 mt-2 font-medium animate-pulse">Uploading image...</p>}
              </div>

              <div className="relative flex py-0.5 items-center">
                <div className="flex-grow border-t border-slate-200"></div>
                <span className="flex-shrink mx-3 text-slate-400 text-[11px] font-medium uppercase tracking-wider">or direct URL</span>
                <div className="flex-grow border-t border-slate-200"></div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Image URL</label>
                <input
                  type="text"
                  placeholder="https://example.com/photo.jpg"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  className="w-full text-sm border rounded-lg p-2.5 focus:border-amber-500"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Alt Text (description)</label>
                <input
                  type="text"
                  placeholder="Image description for SEO"
                  value={imageAlt}
                  onChange={(e) => setImageAlt(e.target.value)}
                  className="w-full text-sm border rounded-lg p-2.5 focus:border-amber-500"
                />
              </div>
              {imageUrl && (
                <div className="mt-2 text-center">
                  <img src={imageUrl} alt="preview" className="max-h-28 mx-auto rounded border" />
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setShowImageModal(false)}
                className="px-4 py-2 border rounded-lg text-xs font-semibold hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={applyImage}
                disabled={!imageUrl}
                className="px-4 py-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-40 text-slate-900 rounded-lg text-xs font-semibold"
              >
                Insert Image
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Dialog: Embed Video */}
      {showVideoModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 space-y-4">
            <div className="flex justify-between items-center border-b pb-3">
              <h4 className="font-semibold text-slate-900 text-base">Embed Video</h4>
              <button onClick={() => setShowVideoModal(false)} className="text-slate-400 hover:text-slate-600">
                <X size={18} />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">YouTube / Video URL</label>
                <input
                  type="text"
                  placeholder="https://www.youtube.com/watch?v=..."
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  className="w-full text-sm border rounded-lg p-2.5 focus:border-amber-500"
                />
                <p className="text-[11px] text-slate-500 mt-1">Supports YouTube or direct iframe embed URL</p>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setShowVideoModal(false)}
                className="px-4 py-2 border rounded-lg text-xs font-semibold hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={applyVideo}
                disabled={!videoUrl}
                className="px-4 py-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-40 text-slate-900 rounded-lg text-xs font-semibold"
              >
                Embed Video
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Media Picker Modal */}
      {showMediaPicker && (
        <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[85vh] flex flex-col overflow-hidden border border-slate-200">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
              <div>
                <h4 className="font-bold text-slate-900 text-base">Select from Uploads</h4>
                <p className="text-xs text-slate-500">Pick an image from your media library or upload a new one</p>
              </div>
              <button
                type="button"
                onClick={() => setShowMediaPicker(false)}
                className="p-1 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-200"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <AdminBlogUploads
                isModal={true}
                onSelectImage={(url) => {
                  setImageUrl(getBlogImageUrl(url));
                  setShowMediaPicker(false);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
